import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Check, Copy, ExternalLink, Rocket } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { useProjectStore } from '@/store/projectStore'
import { useJourneyStore, refreshJourney } from '@/store/journeyStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useJourneyAction } from '@/lib/journeyActions'
import { removeSampleData, setLive } from '@/api/journey'

/**
 * Go live — start using the app for real HERE, in Twasta's development / test
 * environment (the hosted Run view at the project's own address). Nothing is
 * copied to another server; that is Roll out, a later phase (plan §8, §15).
 */
export default function GoLiveTab() {
  const navigate = useNavigate()
  const project = useProjectStore((s) => s.currentProject?.application.folder_name || '')
  const journey = useJourneyStore((s) => s.journey)
  const perms = useProjectPermissions()
  const run = useJourneyAction()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [removeSample, setRemoveSample] = useState(true)
  const [notify, setNotify] = useState(true)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  if (!journey) return <div className="spinner" style={{ margin: '3rem auto' }} />
  const domain = journey.domain || {}
  const canGoLive = !perms.loaded || perms.isOwner || perms.canDeploy
  const required = journey.checks.filter((c) => !c.optional)
  const optional = journey.checks.filter((c) => c.optional)
  const isSite = journey.app_type.startsWith('website')
  const verb = journey.app_type === 'website_public' ? 'Publish' : 'Go live'

  const copyLink = async () => {
    const url = domain.url?.startsWith('/') ? window.location.origin + domain.url : domain.url || ''
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied')
    } catch {
      toast(url, { duration: 8000 })
    }
  }

  const goLive = async () => {
    setBusy(true)
    try {
      if (removeSample && journey.data?.sample_loaded) {
        const rep = await removeSampleData(project)
        if (rep.failed) {
          toast.error(`${rep.failed} sample records could not be removed — check them before going live`)
          return
        }
        toast.success(rep.deleted ? `${rep.deleted} sample records removed` : 'No sample records to remove')
      }
      const res = await setLive(project, { live: true, notify, message: message.trim() || undefined })
      toast.success(isSite ? 'Your website is live' : 'Your app is live')
      if (notify && res.notified) toast.success(`${res.notified} people told it is live`)
      if (res.notify_failed?.length) toast.error(`Could not email: ${res.notify_failed.join(', ')}`)
      setConfirmOpen(false)
      refreshJourney()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const missing = (detail as { missing?: string[] })?.missing
      console.error('[golive] failed', err)
      toast.error(missing?.length ? `Not ready yet: ${missing.join(', ')}`
        : typeof detail === 'string' ? detail : 'Could not go live')
    } finally {
      setBusy(false)
    }
  }

  const takeBack = async () => {
    if (!window.confirm('Mark the app as not live yet? Nothing is deleted and people can still open it.')) return
    try {
      await setLive(project, { live: false })
      refreshJourney()
    } catch (err) {
      console.error('[golive] undo failed', err)
      toast.error('Could not change it')
    }
  }

  const checkRow = (c: typeof journey.checks[number]) => (
    <li key={c.id}>
      <span aria-hidden="true" style={{
        width: 22, height: 22, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
        background: c.done ? 'var(--color-success)' : 'transparent', color: '#ffffff',
        border: c.done ? 'none' : '2px solid var(--color-border-strong)', boxSizing: 'border-box',
      }}>
        {c.done && <Check size={13} strokeWidth={3.2} />}
      </span>
      <span style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{c.label}{c.optional ? <span className="swb-muted" style={{ fontWeight: 400 }}> · optional</span> : null}</span>
        <span className="swb-muted">{c.hint}</span>
      </span>
      <span className="swb-muted" style={{ fontWeight: 600 }}>{c.done ? 'Done' : 'To do'}</span>
      {!c.done && c.action && (
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={() => void run(c.action)}>Do it</button>
      )}
    </li>
  )

  return (
    <div className="swb swb-page">
      <div className="swb-head">
        <div>
          <h1>{verb}</h1>
          <p>{isSite ? 'Everything your website needs before people visit it.' : 'Everything your team needs to start using the app for real.'}</p>
        </div>
      </div>

      {journey.live && (
        <div className="swb-notice swb-notice-info">
          <Rocket size={20} style={{ color: 'var(--color-accent)', flex: 'none' }} />
          <span style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column' }}>
            <strong>{isSite ? 'Your website is live' : 'Your app is live'}</strong>
            <span style={{ fontSize: 14 }}>{journey.summary}. Ask for an improvement whenever you need one.</span>
          </span>
          <button type="button" className="swb-btn swb-btn-primary" onClick={() => void copyLink()}><Copy size={15} /> Share the link</button>
          <button type="button" className="swb-btn swb-btn-secondary" onClick={() => navigate(`/activity/${encodeURIComponent(project)}`)}>Who is using it</button>
          {canGoLive && <button type="button" className="swb-link" onClick={() => void takeBack()}>Not live yet</button>}
        </div>
      )}

      <section className="swb-card swb-card-pad" aria-labelledby="gl-addr" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
        <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 id="gl-addr" className="swb-section-title">Where people open it</h2>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 16, color: 'var(--color-accent-hvr)', wordBreak: 'break-all' }}>
            {domain.address || '—'}
          </span>
          <span className="swb-muted">
            {domain.ready ? 'Ready now.' : 'Being set up.'} You can also use your company’s own address, like erp.yourcompany.com.
          </span>
        </div>
        {domain.url && (
          <a className="swb-btn swb-btn-secondary" href={domain.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={15} /> Open
          </a>
        )}
        <button type="button" className="swb-btn swb-btn-secondary" onClick={() => void run('open:domains')}>Use my own address</button>
      </section>

      <section className="swb-card" aria-labelledby="gl-checks">
        <div className="swb-band"><h2 id="gl-checks">Before you {verb.toLowerCase()}</h2>
          <span className="swb-muted">{required.filter((c) => c.done).length} of {required.length} done</span>
        </div>
        <ul className="swb-list">
          {required.map(checkRow)}
          {optional.map(checkRow)}
        </ul>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '16px 18px', borderTop: '1px solid var(--color-surface-2)' }}>
          <span className="swb-muted">Optional extras: take payments, publish a website, add your logo — in Advanced › Go-live extras.</span>
          {!journey.live && canGoLive && (
            <button type="button" className="swb-btn swb-btn-primary swb-btn-lg" disabled={!journey.go_live_ready}
              title={journey.go_live_ready ? '' : 'Finish the checks above first'}
              onClick={() => { setRemoveSample(true); setConfirmOpen(true) }} data-tw-action="go-live">
              {verb}
            </button>
          )}
        </div>
      </section>

      <div className="swb-notice swb-notice-plain" style={{ fontSize: 14 }}>
        <span style={{ flex: 1 }}>
          <strong>Roll out to a production server</strong> — coming later. Going live here keeps the app on
          Twasta; moving it to your own production server will be a separate step.
        </span>
      </div>

      {confirmOpen && (
        <Modal
          open
          onClose={() => { if (!busy) setConfirmOpen(false) }}
          title={isSite ? 'Put the website live' : 'Start using the app for real'}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => void goLive()} disabled={busy}>
                {busy ? 'Working…' : verb}
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0 }}>People open it at <strong>{domain.address}</strong>.</p>
            {journey.data?.sample_loaded && (
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={removeSample} onChange={(e) => setRemoveSample(e.target.checked)} style={{ marginTop: 3 }} />
                <span><strong>Remove the sample data first</strong>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Deletes the example records, newest first. Records your team added are kept.
                  </span></span>
              </label>
            )}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ marginTop: 3 }} />
              <span><strong>Email everyone with access the link</strong>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  People still to accept an invitation already have theirs.
                </span></span>
            </label>
            {notify && (
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="A note to add to the email (optional)" aria-label="A note to add to the email" />
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
